/** A live document owns its persisted tab identity and its private admission sequence. */
const TAB_KEY = 'ft-ticket-queue-tab';
export const OFFLINE_TAB_READY_EVENT = 'ft:offline-tab-ready';
const tabStorage = (() => { try { return sessionStorage; } catch { return undefined; } })();
let restored = false;
let tabId: string;
try {
  const saved = tabStorage?.getItem(TAB_KEY);
  restored = Boolean(saved);
  tabId = saved || crypto.randomUUID();
  tabStorage?.setItem(TAB_KEY, tabId);
} catch { tabId = crypto.randomUUID(); }
let sequence = 0;
let ready = !restored; // A new UUID cannot have existing entries.
let initialization: Promise<void> | undefined;
let releaseLifetime: (() => void) | undefined;
const documentId = crypto.randomUUID();
const DOCUMENT_PREFIX = 'ft-ticket-queue-document:';

export function ticketQueueTabId(): string { return tabId; }
export function isTicketQueueTabReady(): boolean { return ready; }
export function nextTicketQueueSequence(): number {
  if (!ready) throw new Error('Ticket snapshots are still loading');
  return ++sequence;
}
export function observeOwnTicketQueueEntry(entry: { tabId: string; sequence: number }): void {
  if (entry.tabId === tabId) sequence = Math.max(sequence, entry.sequence);
}

const lockName = (id: string) => `ft-ticket-queue-tab:${id}`;
async function claimLifetime(): Promise<boolean> {
  if (!navigator.locks) {
    // Unique document markers detect copied sessionStorage. A crashed document
    // may leave a marker: choosing a fresh UUID is safer than reusing its keys.
    const key = DOCUMENT_PREFIX + tabId + ':' + documentId;
    try {
      localStorage.setItem(key, 'alive');
      const foreign = Object.keys(localStorage).some((other) => other.startsWith(DOCUMENT_PREFIX + tabId + ':') && other !== key);
      if (foreign) { localStorage.removeItem(key); return false; }
      releaseLifetime = () => { try { localStorage.removeItem(key); } catch { /* No payload lives in this marker. */ } };
      return true;
    } catch {
      // No reliable way to distinguish a reload from a duplicated live tab.
      if (restored) { restored = false; freshIdentity(); }
      return true;
    }
  }
  return new Promise<boolean>((resolve) => {
    void navigator.locks.request(lockName(tabId), { ifAvailable: true }, async (lock) => {
      if (!lock) { resolve(false); return; }
      await new Promise<void>((release) => { releaseLifetime = release; resolve(true); });
    }).catch(() => resolve(false));
  });
}
function freshIdentity(): void {
  releaseLifetime?.();
  tabId = crypto.randomUUID();
  sequence = 0;
  restored = false;
  try { tabStorage?.setItem(TAB_KEY, tabId); } catch { /* Page-lifetime identity still isolates entries. */ }
}
export function initializeTicketQueueTab(load: () => Promise<{ tabId: string; sequence: number }[]>): Promise<void> {
  initialization ??= (async () => {
    // sessionStorage can be copied by an opener: a held lock proves this is a
    // second live document, which must obtain a distinct identity.
    if (!await claimLifetime()) { freshIdentity(); await claimLifetime(); }
    try { for (const entry of await load()) observeOwnTicketQueueEntry(entry); } catch {
      // Never reuse unknown keys after a reload whose entries cannot be read.
      if (restored && !ready) { freshIdentity(); await claimLifetime(); }
    }
    ready = true;
    window.dispatchEvent(new Event(OFFLINE_TAB_READY_EVENT));
  })();
  return initialization;
}

/** Hold the departed tab's lock throughout replay; live tabs are never taken over. */
export async function withDepartedTicketQueueTab<T>(id: string, blocked: T, work: () => Promise<T>): Promise<T> {
  if (id === tabId || id.startsWith('legacy:')) return work();
  if (!navigator.locks) {
    // Without a reliable liveness primitive, foreign edits wait for explicit
    // review/export/discard. Synthetic migration identities never had a page.
    return blocked;
  }
  return navigator.locks.request(lockName(id), { ifAvailable: true }, (lock) => lock ? work() : blocked);
}
window.addEventListener('pagehide', (event) => {
  if (!(event as PageTransitionEvent).persisted) releaseLifetime?.();
});
